import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Footer from './common/Footer';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Custom Tooltip component
const Tooltip = ({ children }) => (
  <div className="group relative inline-block">
    {children}
    <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute z-50 w-64 p-2 mt-2 text-sm text-gray-200 bg-gray-800 rounded-md border border-gray-700 shadow-lg">
      To change your email address contact administrator
    </div>
  </div>
);

const Settings = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const userEmail = localStorage.getItem('email') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      const email = localStorage.getItem('email');
      await axios.post(
        `${API_BASE_URL}/api/reset-password`,
        {
          email,
          currentPassword,
          newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err.code === 'ERR_NETWORK') {
        // Try alternate port if default port fails
        try {
          await axios.post(
            'http://localhost:3002/api/reset-password',
            {
              email: localStorage.getItem('email'),
              currentPassword,
              newPassword,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
            }
          );
          setSuccess('Password updated successfully');
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        } catch (retryErr) {
          setError(retryErr.response?.data?.error || 'An error occurred');
        }
      } else {
        setError(err.response?.data?.error || 'An error occurred');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Background Image */}
      <div className="absolute inset-0 z-0 opacity-10">
        <img
          src="/images/military plane cartoon illustration_4704680.png"
          alt="Military Plane"
          className="w-full h-full object-cover"
        />
      </div>

      <nav className="bg-gray-800/90 border-b border-gray-700 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <img
                  src="/images/military plane cartoon illustration_4704680.png"
                  alt="Plane Logo"
                  className="h-8 w-auto mr-3"
                />
                <h1 className="text-xl font-bold text-yellow-500">Settings</h1>
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="ml-4 px-4 py-2 text-sm text-gray-300 hover:text-yellow-500 transition-colors duration-200"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-grow py-6 sm:py-8 lg:py-10 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl w-full mx-auto">
          <div className="bg-gray-800/90 shadow-lg rounded-lg p-6 sm:p-8 lg:p-10 border border-gray-700 backdrop-blur-sm">
            <h2 className="text-2xl font-bold mb-6 text-yellow-500">Reset Password</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email field with tooltip */}
              <div className="flex items-center space-x-2">
                <div className="flex-grow">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-300 mb-1"
                  >
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={userEmail}
                    readOnly
                    className="mt-1 block w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-gray-400 cursor-not-allowed focus:outline-none transition-colors duration-200"
                  />
                </div>
                <Tooltip>
                  <div className="mt-7">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-gray-400 hover:text-yellow-500 transition-colors duration-200"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </Tooltip>
              </div>

              <div>
                <label
                  htmlFor="current-password"
                  className="block text-sm font-medium text-gray-300 mb-1"
                >
                  Current Password
                </label>
                <input
                  id="current-password"
                  type="password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1 block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors duration-200"
                />
              </div>

              <div>
                <label
                  htmlFor="new-password"
                  className="block text-sm font-medium text-gray-300 mb-1"
                >
                  New Password
                </label>
                <input
                  id="new-password"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors duration-200"
                />
              </div>

              <div>
                <label
                  htmlFor="confirm-password"
                  className="block text-sm font-medium text-gray-300 mb-1"
                >
                  Confirm New Password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors duration-200"
                />
              </div>

              {error && (
                <div className="p-4 bg-red-900/50 border border-red-700 rounded-md text-red-200 text-sm">
                  {error}
                </div>
              )}

              {success && (
                <div className="p-4 bg-green-900/50 border border-green-700 rounded-md text-green-200 text-sm">
                  {success}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-900 bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200"
                >
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      <footer className="flex-shrink-0 mt-auto">
        <Footer />
      </footer>
    </div>
  );
};

export default Settings;